import React, { FunctionComponent, useEffect, useState } from 'react';
import { Heading } from '../../../shared/Heading';
import { Paragraph } from '../../../shared/Paragraph';
import { useAccurateWindowWidth } from '../../../../hooks/useDeviceInfo';
import LazyLoadedImage from '../../../Media/LazyLoadedImage';
import { MergedProduct, PurchaseModalState } from '../types';
import { StarIcon } from '../../../shared/SVG/Icons/StarIcon';
import { Button } from '../../../shared/Button';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { joinStrings } from '../../../../utils/string';
import { PurchaseModal } from '../PurchaseModal';
import { useUserContext } from '../../../../providers/UserProvider';
import { useAuth } from 'react-oidc-context';
import { useSignIn } from '../../../../hooks/auth';
import { storageClaimedProductId } from '../../../../utils/storage';
import { purchaseProduct } from '../service';
import { getActualXPToken } from '../../../../utils/auth';
import { trackEvent } from '../../../../tracking/tracking';
import './ItvShopCard.scss';

type ItvShopCardProps = { product: MergedProduct };

export const ItvShopCard: FunctionComponent<ItvShopCardProps> = ({ product }): JSX.Element => {
  const { t } = useTranslations();
  const { isMobile } = useAccurateWindowWidth();
  const {
    starBalance: { number: numberStarBalance },
    XPToken,
    setXPToken,
    updateStarBalance,
    setUpdateStarBalance,
  } = useUserContext();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const signIn = useSignIn();

  const {
    productId,
    title,
    description,
    desktopImage,
    mobileImage,
    subtext,
    badge,
    price,
    colorScheme,
    ctaUrl,
    ctaCopy,
  } = product;

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalState, setModalState] = useState<PurchaseModalState>('review');
  const [claimedProductId, setClaimedProductId] = useState<string>(productId || '');

  const resultImg = String(isMobile ? mobileImage : desktopImage);

  useEffect(() => {
    if (!isAuthLoading) {
      const storedClaimedProductId = storageClaimedProductId.get();

      if (productId === storedClaimedProductId) {
        storageClaimedProductId.remove();

        if (isAuthenticated) {
          setClaimedProductId(storedClaimedProductId as string);
          setShowModal(true);
        }
      }
    }
  }, [isAuthLoading]);

  useEffect(() => {
    showModal && modalState === 'review' && trackEvent({ event: 'purchase_initiated' });
  }, [showModal]);

  const onCTAClick = (): void => {
    if (isAuthenticated) {
      Number(price) > numberStarBalance && setModalState('insufficient');

      setShowModal(true);
    } else {
      storageClaimedProductId.set({ value: productId });
      signIn({ redirectUri: window.location.href });
    }
  };

  const onPurchaseConfirm = async (): Promise<void> => {
    if (XPToken) {
      try {
        const activeToken = await getActualXPToken(XPToken, setXPToken);

        await purchaseProduct(claimedProductId, activeToken);

        setUpdateStarBalance(!updateStarBalance);
        setModalState('success');

        trackEvent({ event: 'purchase_complete' });
      } catch (error) {
        setModalState('error');
      }
    }
  };

  const onClosePurchaseModal = (): void => {
    setShowModal(false);
    setModalState('review');
  };

  return (
    <>
      <div className={joinStrings(['itv-shop-card', colorScheme && `itv-shop-card--${colorScheme}`])}>
        <div className="itv-shop-card__image-block">
          {badge && <div className="itv-shop-card__badge">{badge}</div>}
          <LazyLoadedImage src={resultImg} alt="product" className="itv-shop-card__img" />
        </div>
        <div className="itv-shop-card__content">
          <div className="itv-shop-card__titles">
            <Heading className="itv-shop-card__title" noMargin level={4}>
              {title}
            </Heading>
            <Paragraph className="itv-shop-card__description" noMargin>
              {description}
            </Paragraph>
            {subtext && (
              <Paragraph className="itv-shop-card__sub-text" noMargin>
                {subtext}
              </Paragraph>
            )}
          </div>
          <div className="itv-shop-card__action-items">
            <div className="itv-shop-card__star-balance">
              {price && (
                <>
                  <span>{price}</span> <StarIcon />
                </>
              )}
            </div>

            <Button className="itv-shop-card__purchase-button" type="button" onClick={onCTAClick}>
              {t('shop.cta-text')}
            </Button>
          </div>
        </div>
      </div>
      {showModal && (
        <PurchaseModal
          isOpen
          onClose={onClosePurchaseModal}
          state={modalState}
          itemTitle={title}
          itemSubtitle={description}
          itemGameName={subtext}
          currentBalance={numberStarBalance}
          cost={price}
          onConfirm={onPurchaseConfirm}
          confirmationCTAUrl={ctaUrl}
          confirmationCTACopy={ctaCopy}
        />
      )}
    </>
  );
};
